/**
 * RTWO Native Currency Engine — Domain Models
 * Phase 14.5 | Module ID: RTWO-NATV-001
 * Version: 1.0.0-phase14.5
 *
 * Strongly typed domain models for the RTWO Native Currency Engine.
 * Defines the dedicated protocol engine for the RTWO native currency.
 *
 * Truthful Data Policy:
 * - No fake RTWO supply, market cap, price, treasury balance, validator rewards,
 *   staking supply, or allocations.
 * - RTWO currency is NOT claimed as live.
 * - Native Currency Engine is in Architecture Phase — Structure Complete.
 * - All runtime values are null until Genesis Configuration and Ledger Connection are established.
 *
 * Source: Native Currency Engine (RTWO-NATV-001)
 */

// ─────────────────────────────────────────────
// PRIMITIVE ID TYPES
// ─────────────────────────────────────────────

export type CurrencyId = string;
export type PolicyId = string;
export type SupplyStateId = string;
export type AllocationId = string;
export type FeePolicyId = string;
export type ScheduleId = string;
export type EventId = string;
export type ParameterId = string;
export type NATVTimestamp = number | null;

export const NATV_ENGINE_VERSION = {
  moduleId: 'RTWO-NATV-001',
  moduleName: 'RTWO Native Currency Engine',
  version: '1.0.0-phase14.5',
  phase: 'Phase 14.5 — RTWO Native Currency Engine',
  status: 'Structure Complete — Waiting for Genesis Configuration',
  productionReadiness: 'Pending Genesis Configuration and Ledger Connection',
  policy:
    'Truthful Data Policy Enforced — No Fake RTWO Supply, Market Cap, Price, Treasury Balance, Validator Rewards, Staking Supply, or Allocations',
} as const;

// ─────────────────────────────────────────────
// NATIVE CURRENCY ENGINE STATUS TYPES
// ─────────────────────────────────────────────

export type RTWONativeCurrencyEngineStatus =
  | 'structure_ready'
  | 'waiting_for_genesis_configuration'
  | 'waiting_for_real_ledger_data'
  | 'supply_not_configured'
  | 'monetary_policy_pending'
  | 'mainnet_not_deployed'
  | 'genesis_pending'
  | 'genesis_allocated'
  | 'active'
  | 'governance_controlled'
  | 'production_pending'
  | 'archived';

export const NATV_ENGINE_STATUS_LABELS: Record<RTWONativeCurrencyEngineStatus, string> = {
  structure_ready: 'RTWO Native Currency Structure Ready',
  waiting_for_genesis_configuration: 'Waiting for Genesis Configuration',
  waiting_for_real_ledger_data: 'Waiting for Real Ledger Data',
  supply_not_configured: 'Supply Not Configured',
  monetary_policy_pending: 'Monetary Policy Pending',
  mainnet_not_deployed: 'Mainnet Not Deployed',
  genesis_pending: 'Genesis Pending',
  genesis_allocated: 'Genesis Allocated',
  active: 'Active',
  governance_controlled: 'Governance Controlled',
  production_pending: 'Production Pending',
  archived: 'Archived',
};

// ─────────────────────────────────────────────
// NATIVE CURRENCY LIFECYCLE
// ─────────────────────────────────────────────

export type RTWONativeCurrencyLifecycleStage =
  | 'defined'
  | 'validated'
  | 'genesis_pending'
  | 'genesis_allocated'
  | 'active'
  | 'governance_controlled'
  | 'archived';

export const NATV_LIFECYCLE_LABELS: Record<RTWONativeCurrencyLifecycleStage, string> = {
  defined: 'Defined',
  validated: 'Validated',
  genesis_pending: 'Genesis Pending',
  genesis_allocated: 'Genesis Allocated',
  active: 'Active',
  governance_controlled: 'Governance Controlled',
  archived: 'Archived',
};

export const NATV_LIFECYCLE_DESCRIPTIONS: Record<RTWONativeCurrencyLifecycleStage, string> = {
  defined: 'Native currency definition created — architecture phase, not live',
  validated: 'Currency definition validated — awaiting genesis configuration',
  genesis_pending: 'Genesis configuration pending — mainnet not deployed',
  genesis_allocated: 'Genesis allocation defined — awaiting ledger connection',
  active: 'Native currency active on the RTWO ledger — requires real ledger connection',
  governance_controlled: 'Currency parameters under governance control — future phase',
  archived: 'Currency archived — read-only history',
};

// ─────────────────────────────────────────────
// CURRENCY TYPE
// ─────────────────────────────────────────────

export type RTWOCurrencyType =
  | 'native_protocol_currency'
  | 'governance_currency'
  | 'staking_currency'
  | 'fee_currency'
  | 'not_configured';

export const CURRENCY_TYPE_LABELS: Record<RTWOCurrencyType, string> = {
  native_protocol_currency: 'Native Protocol Currency',
  governance_currency: 'Governance Currency',
  staking_currency: 'Staking Currency',
  fee_currency: 'Fee Currency',
  not_configured: 'Not Configured',
};

// ─────────────────────────────────────────────
// DOMAIN MODEL 1: RTWONativeCurrency
// ─────────────────────────────────────────────

export type RTWONativeCurrencyStatus =
  | 'structure_ready'
  | 'waiting_for_genesis'
  | 'genesis_pending'
  | 'genesis_allocated'
  | 'active'
  | 'governance_controlled'
  | 'archived';

export interface RTWONativeCurrency {
  currencyId: CurrencyId;
  symbol: string;
  name: string;
  decimals: number | null;
  currencyType: RTWOCurrencyType;
  status: RTWONativeCurrencyStatus;
  createdAt: NATVTimestamp;
}

// ─────────────────────────────────────────────
// MONETARY POLICY TYPES
// ─────────────────────────────────────────────

export type RTWOSupplyModel =
  'fixed_supply' | 'capped_supply' | 'inflationary' | 'deflationary' | 'hybrid' | 'not_configured';

export type RTWOIssuancePolicy =
  'genesis_only' | 'block_reward' | 'governance_controlled' | 'not_configured';

export type RTWOBurnPolicy = 'no_burn' | 'fee_burn' | 'governance_burn' | 'not_configured';

export type RTWOInflationPolicy =
  'no_inflation' | 'fixed_rate' | 'dynamic_rate' | 'governance_controlled' | 'not_configured';

export type RTWODeflationPolicy =
  'no_deflation' | 'fee_burn_deflation' | 'governance_deflation' | 'not_configured';

export type RTWOMonetaryPolicyStatus =
  | 'architecture_ready'
  | 'monetary_policy_pending'
  | 'not_configured'
  | 'active'
  | 'governance_controlled'
  | 'deprecated';

// ─────────────────────────────────────────────
// DOMAIN MODEL 2: RTWOMonetaryPolicy
// ─────────────────────────────────────────────

export interface RTWOMonetaryPolicy {
  policyId: PolicyId;
  currencyId: CurrencyId;
  supplyModel: RTWOSupplyModel;
  issuancePolicy: RTWOIssuancePolicy;
  burnPolicy: RTWOBurnPolicy;
  inflationPolicy: RTWOInflationPolicy;
  deflationPolicy: RTWODeflationPolicy;
  status: RTWOMonetaryPolicyStatus;
}

// ─────────────────────────────────────────────
// SUPPLY STATE TYPES
// ─────────────────────────────────────────────

export type RTWOSupplyStateStatus =
  | 'supply_not_configured'
  | 'waiting_for_genesis'
  | 'waiting_for_real_ledger_data'
  | 'genesis_pending'
  | 'active'
  | 'deprecated';

// ─────────────────────────────────────────────
// DOMAIN MODEL 3: RTWOSupplyState
// ─────────────────────────────────────────────

export interface RTWOSupplyState {
  supplyStateId: SupplyStateId;
  currencyId: CurrencyId;
  /** Reference only — no fake supply values */
  totalSupplyReference: string;
  /** Reference only — no fake circulating supply */
  circulatingSupplyReference: string;
  /** Reference only — no fake locked supply */
  lockedSupplyReference: string;
  /** Reference only — no fake staked supply */
  stakedSupplyReference: string;
  /** Reference only — no fake treasury balance */
  treasurySupplyReference: string;
  status: RTWOSupplyStateStatus;
  updatedAt: NATVTimestamp;
}

// ─────────────────────────────────────────────
// ALLOCATION POLICY TYPES
// ─────────────────────────────────────────────

export type RTWOAllocationType =
  | 'genesis_allocation'
  | 'validator_rewards'
  | 'staking_rewards'
  | 'treasury'
  | 'ecosystem_fund'
  | 'team_allocation'
  | 'community_allocation'
  | 'not_configured';

export const ALLOCATION_TYPE_LABELS: Record<RTWOAllocationType, string> = {
  genesis_allocation: 'Genesis Allocation',
  validator_rewards: 'Validator Rewards',
  staking_rewards: 'Staking Rewards',
  treasury: 'Treasury',
  ecosystem_fund: 'Ecosystem Fund',
  team_allocation: 'Team Allocation',
  community_allocation: 'Community Allocation',
  not_configured: 'Not Configured',
};

export type RTWOAllocationPolicyStatus =
  | 'architecture_ready'
  | 'not_configured'
  | 'waiting_for_genesis'
  | 'active'
  | 'vesting'
  | 'completed'
  | 'deprecated';

// ─────────────────────────────────────────────
// DOMAIN MODEL 4: RTWOAllocationPolicy
// ─────────────────────────────────────────────

export interface RTWOAllocationPolicy {
  allocationId: AllocationId;
  currencyId: CurrencyId;
  allocationType: RTWOAllocationType;
  /** Reference only — no fake allocation values */
  allocationReference: string;
  /** Reference only — no fake vesting schedules */
  vestingPolicyReference: string;
  status: RTWOAllocationPolicyStatus;
}

// ─────────────────────────────────────────────
// FEE POLICY TYPES
// ─────────────────────────────────────────────

export type RTWOBaseFeePolicy =
  | 'fixed_base_fee'
  | 'dynamic_base_fee'
  | 'eip1559_style'
  | 'governance_controlled'
  | 'not_configured';

export type RTWOPriorityFeePolicy =
  'no_priority_fee' | 'optional_priority_fee' | 'required_priority_fee' | 'not_configured';

export type RTWOBurnFeePolicy =
  'no_fee_burn' | 'partial_fee_burn' | 'full_fee_burn' | 'governance_controlled' | 'not_configured';

export type RTWOValidatorFeePolicy =
  | 'full_validator_fee'
  | 'partial_validator_fee'
  | 'no_validator_fee'
  | 'governance_controlled'
  | 'not_configured';

export type RTWOFeePolicyStatus =
  'architecture_ready' | 'not_configured' | 'active' | 'governance_controlled' | 'deprecated';

// ─────────────────────────────────────────────
// DOMAIN MODEL 5: RTWOFeePolicy
// ─────────────────────────────────────────────

export interface RTWOFeePolicy {
  feePolicyId: FeePolicyId;
  currencyId: CurrencyId;
  baseFeePolicy: RTWOBaseFeePolicy;
  priorityFeePolicy: RTWOPriorityFeePolicy;
  burnFeePolicy: RTWOBurnFeePolicy;
  validatorFeePolicy: RTWOValidatorFeePolicy;
  status: RTWOFeePolicyStatus;
}

// ─────────────────────────────────────────────
// EMISSION SCHEDULE TYPES
// ─────────────────────────────────────────────

export type RTWOEmissionType =
  | 'genesis_emission'
  | 'block_reward_emission'
  | 'staking_reward_emission'
  | 'governance_emission'
  | 'not_configured';

export const EMISSION_TYPE_LABELS: Record<RTWOEmissionType, string> = {
  genesis_emission: 'Genesis Emission',
  block_reward_emission: 'Block Reward Emission',
  staking_reward_emission: 'Staking Reward Emission',
  governance_emission: 'Governance Emission',
  not_configured: 'Not Configured',
};

export type RTWOEmissionScheduleStatus =
  | 'architecture_ready'
  | 'not_configured'
  | 'waiting_for_genesis'
  | 'active'
  | 'completed'
  | 'deprecated';

// ─────────────────────────────────────────────
// DOMAIN MODEL 6: RTWOEmissionSchedule
// ─────────────────────────────────────────────

export interface RTWOEmissionSchedule {
  scheduleId: ScheduleId;
  currencyId: CurrencyId;
  emissionType: RTWOEmissionType;
  /** Reference only — no fake emission amounts */
  emissionReference: string;
  /** Reference only — no fake start block/time */
  startReference: string;
  /** Reference only — no fake end block/time */
  endReference: string;
  status: RTWOEmissionScheduleStatus;
}

// ─────────────────────────────────────────────
// NATIVE CURRENCY EVENT TYPES
// ─────────────────────────────────────────────

export type RTWONativeCurrencyEventType =
  | 'currency_defined'
  | 'currency_validated'
  | 'genesis_pending'
  | 'genesis_allocated'
  | 'currency_activated'
  | 'monetary_policy_configured'
  | 'supply_state_updated'
  | 'allocation_configured'
  | 'fee_policy_configured'
  | 'emission_schedule_configured'
  | 'parameter_updated'
  | 'governance_transition'
  | 'currency_archived';

export type RTWONativeCurrencyEventStatus =
  'architecture_ready' | 'not_emitted' | 'emitted' | 'failed';

// ─────────────────────────────────────────────
// DOMAIN MODEL 7: RTWONativeCurrencyEvent
// ─────────────────────────────────────────────

export interface RTWONativeCurrencyEvent {
  eventId: EventId;
  currencyId: CurrencyId;
  eventType: RTWONativeCurrencyEventType;
  /** Reference only — no fake payload data */
  payloadReference: string;
  sourceModule: string;
  status: RTWONativeCurrencyEventStatus;
  emittedAt: NATVTimestamp;
}

// ─────────────────────────────────────────────
// ECONOMIC PARAMETER TYPES
// ─────────────────────────────────────────────

export type RTWOParameterType =
  | 'supply_parameter'
  | 'fee_parameter'
  | 'emission_parameter'
  | 'staking_parameter'
  | 'governance_parameter'
  | 'not_configured';

export const PARAMETER_TYPE_LABELS: Record<RTWOParameterType, string> = {
  supply_parameter: 'Supply Parameter',
  fee_parameter: 'Fee Parameter',
  emission_parameter: 'Emission Parameter',
  staking_parameter: 'Staking Parameter',
  governance_parameter: 'Governance Parameter',
  not_configured: 'Not Configured',
};

export type RTWOParameterStatus =
  'architecture_ready' | 'not_configured' | 'active' | 'governance_controlled' | 'deprecated';

// ─────────────────────────────────────────────
// DOMAIN MODEL 8: RTWOProtocolEconomicParameter
// ─────────────────────────────────────────────

export interface RTWOProtocolEconomicParameter {
  parameterId: ParameterId;
  parameterName: string;
  parameterType: RTWOParameterType;
  /** Reference only — no fake parameter values */
  valueReference: string;
  governanceControlled: boolean;
  status: RTWOParameterStatus;
}

// ─────────────────────────────────────────────
// ARCHITECTURE-DEFINED ECONOMIC PARAMETERS
// ─────────────────────────────────────────────

export const RTWO_ECONOMIC_PARAMETERS: RTWOProtocolEconomicParameter[] = [
  {
    parameterId: 'param-001',
    parameterName: 'Total Supply Cap',
    parameterType: 'supply_parameter',
    valueReference: 'Source: Genesis Configuration — Not Created',
    governanceControlled: false,
    status: 'not_configured',
  },
  {
    parameterId: 'param-002',
    parameterName: 'Base Fee Rate',
    parameterType: 'fee_parameter',
    valueReference: 'Source: Governance Runtime — Future Phase',
    governanceControlled: true,
    status: 'not_configured',
  },
  {
    parameterId: 'param-003',
    parameterName: 'Block Reward Rate',
    parameterType: 'emission_parameter',
    valueReference: 'Source: Genesis Configuration — Not Created',
    governanceControlled: false,
    status: 'not_configured',
  },
  {
    parameterId: 'param-004',
    parameterName: 'Minimum Staking Amount',
    parameterType: 'staking_parameter',
    valueReference: 'Source: Governance Runtime — Future Phase',
    governanceControlled: true,
    status: 'not_configured',
  },
  {
    parameterId: 'param-005',
    parameterName: 'Staking Reward Rate',
    parameterType: 'staking_parameter',
    valueReference: 'Source: Governance Runtime — Future Phase',
    governanceControlled: true,
    status: 'not_configured',
  },
  {
    parameterId: 'param-006',
    parameterName: 'Fee Burn Percentage',
    parameterType: 'fee_parameter',
    valueReference: 'Source: Governance Runtime — Future Phase',
    governanceControlled: true,
    status: 'not_configured',
  },
  {
    parameterId: 'param-007',
    parameterName: 'Validator Fee Share',
    parameterType: 'fee_parameter',
    valueReference: 'Source: Governance Runtime — Future Phase',
    governanceControlled: true,
    status: 'not_configured',
  },
  {
    parameterId: 'param-008',
    parameterName: 'Governance Quorum Threshold',
    parameterType: 'governance_parameter',
    valueReference: 'Source: Governance Runtime — Future Phase',
    governanceControlled: true,
    status: 'not_configured',
  },
];

// ─────────────────────────────────────────────
// NATIVE CURRENCY EVENT HANDLER
// ─────────────────────────────────────────────

export type NATVEventType =
  | 'currency_lifecycle_changed'
  | 'monetary_policy_updated'
  | 'supply_state_updated'
  | 'allocation_updated'
  | 'fee_policy_updated'
  | 'emission_schedule_updated'
  | 'parameter_updated';

export type NATVEventHandler = (eventType: NATVEventType, payload: Record<string, unknown>) => void;
